import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Trophy, Medal, BookOpen, Sparkle } from "@phosphor-icons/react";

const MEDAL_COLORS = ["#f59e0b", "#94a3b8", "#d97706"];

export default function Leaderboard() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/leaderboard").then((r) => setRows(r.data)).catch(() => {}).finally(() => setLoading(false));
  }, []);

  return (
    <div className="max-w-3xl mx-auto px-4 sm:px-6 py-12" data-testid="leaderboard-page">
      <div className="text-center mb-10">
        <span className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-brand-sun/60 text-ink font-semibold text-sm">
          <Trophy size={16} weight="fill" className="text-brand-coral" /> This week's stars
        </span>
        <h1 className="mt-4 font-heading font-bold text-4xl sm:text-5xl text-ink tracking-tight">Reader Leaderboard</h1>
        <p className="font-body text-ink-soft mt-3 text-lg">The most lessons finished in the last 7 days. Way to go, little readers!</p>
      </div>

      {loading ? (
        <p className="text-center font-body text-ink-soft">Loading…</p>
      ) : rows.length === 0 ? (
        <div className="bg-white sticker rounded-3xl p-12 text-center" data-testid="leaderboard-empty">
          <Sparkle size={40} weight="fill" className="mx-auto text-brand-sun mb-3" />
          <p className="font-heading font-bold text-xl text-ink">No lessons finished yet this week</p>
          <p className="font-body text-ink-soft mt-1">Be the first to light up the leaderboard!</p>
          <Link to="/library"><Button className="mt-6 rounded-full bg-brand-sky hover:bg-brand-skylight text-white font-bold px-8 py-6">
            <BookOpen size={20} weight="duotone" className="mr-2" /> Start reading
          </Button></Link>
        </div>
      ) : (
        <div className="space-y-3">
          {rows.map((r, i) => (
            <motion.div
              key={i} initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.05 }}
              className="bg-white sticker rounded-3xl p-4 sm:p-5 flex items-center gap-4"
              data-testid={`leaderboard-row-${i}`}
            >
              <span className="grid place-items-center w-10 h-10 rounded-2xl font-heading font-bold text-lg shrink-0"
                style={{ backgroundColor: i < 3 ? `${MEDAL_COLORS[i]}22` : "#e2e8f0", color: i < 3 ? MEDAL_COLORS[i] : "#64748b" }}>
                {i < 3 ? <Medal size={22} weight="fill" /> : i + 1}
              </span>
              {r.picture ? (
                <img src={r.picture} alt="" className="w-11 h-11 rounded-full object-cover" />
              ) : (
                <span className="grid place-items-center w-11 h-11 rounded-full bg-brand-sky/15 text-brand-sky font-bold text-lg">{r.name[0]}</span>
              )}
              <p className="flex-1 font-heading font-semibold text-ink text-lg">{r.name}</p>
              <span className="inline-flex items-center gap-1.5 font-body font-bold text-ink">
                <BookOpen size={18} weight="duotone" className="text-brand-grass" /> {r.lessons}
                <span className="font-normal text-ink-soft text-sm">lessons</span>
              </span>
            </motion.div>
          ))}
        </div>
      )}
    </div>
  );
}
